import { useState, useEffect, useRef } from 'react';

interface ExperimentState {
  variantName: string | null;
  isLoading: boolean;
}

/**
 * Assigns the current visitor to an experiment variant and tracks impressions automatically.
 *
 * Assignment is deterministic per session (based on server-side session ID) and cached in
 * sessionStorage to avoid redundant network calls. Impressions are deduplicated server-side.
 *
 * @param slug - The experiment slug (e.g. 'landing-page-headline')
 * @returns variantName (null if experiment not running), isLoading, and trackConversion()
 *
 * Usage:
 *   const { variantName } = useExperiment('landing-page-headline');
 *   const headline = variantName === 'treatment'
 *     ? 'Say goodbye to spreadsheet based mentorship systems'
 *     : 'The platform for growing teams';
 */
export function useExperiment(slug: string) {
    const [state, setState] = useState<ExperimentState>({ variantName: null, isLoading: true });
    const impressionTracked = useRef(false);

    useEffect(() => {
        const cacheKey = `exp:${slug}`;
        const cached = sessionStorage.getItem(cacheKey);

        if (cached !== null) {
            setState({ variantName: cached === '' ? null : cached, isLoading: false });
            return;
        }

        fetch(`/api/experiments/${encodeURIComponent(slug)}/assign`)
            .then((r) => r.json())
            .then((data) => {
                const variantName: string | null = data?.data?.variantName ?? null;
                sessionStorage.setItem(cacheKey, variantName ?? '');
                setState({ variantName, isLoading: false });
            })
            .catch(() => {
                setState({ variantName: null, isLoading: false });
            });
    }, [slug]);

    // Track impression once per hook mount (server deduplicates per session)
    useEffect(() => {
        if (!state.isLoading && state.variantName && !impressionTracked.current) {
            impressionTracked.current = true;
            fetch('/api/experiments/track', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ experimentSlug: slug, event: 'impression' })
            }).catch(() => {});
        }
    }, [slug, state.isLoading, state.variantName]);

    const trackConversion = (metadata?: Record<string, unknown>) => {
        fetch('/api/experiments/track', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ experimentSlug: slug, event: 'conversion', metadata })
        }).catch(() => {});
    };

    return { ...state, trackConversion };
}
